import type { Task } from "../../api.ts";
import { useTheme } from "../../glassTheme.tsx";

/** Tier is a dot, never a colour (§0 tokens rule): filled must, ring should, faint ring nice. */
export function TierDot({ tier }: { tier: Task["tier"] }) {
  const { colors } = useTheme();
  return (
    <span style={{
      display: "inline-block", width: 9, height: 9, borderRadius: "50%", marginRight: 8,
      verticalAlign: -0.5, boxSizing: "border-box", flex: "none",
      border: `1.5px solid ${tier === "nice" ? colors.hair2 : colors.ink}`,
      background: tier === "must" ? colors.ink : "transparent",
    }} />
  );
}

/**
 * The one row grid used across Sessions and (later) Board and Money: a 3px
 * value-tint rail, title plus one meta line, one right-hand figure (§0
 * "list rows: task, money, journal").
 */
export function TaskRow({ task, onOpen, right, meta }: {
  task: Task; onOpen: () => void;
  /** Right-aligned figure — usually the date. */
  right?: string | null;
  /** Defaults to the breadcrumb; callers with something more specific (a lead-time row) pass their own. */
  meta?: string;
}) {
  const { colors } = useTheme();
  const tint = task.value_id ? (colors.tint as Record<string, string>)[task.value_id] : undefined;
  return (
    <div
      onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      style={{
        display: "grid", gridTemplateColumns: "3px 1fr auto", gap: 12, alignItems: "center",
        padding: "12px 0", borderBottom: `1px solid ${colors.hair}`, cursor: "pointer",
      }}
    >
      <span style={{ width: 3, height: 22, borderRadius: 2, background: tint ?? colors.hair2, justifySelf: "center" }} />
      <div style={{ lineHeight: 1.3, minWidth: 0 }}>
        <div style={{ fontSize: 15, color: colors.ink, display: "flex", alignItems: "center" }}>
          <TierDot tier={task.tier} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.title}</span>
        </div>
        <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 2 }}>{meta ?? task.breadcrumb}</div>
      </div>
      <div style={{ fontSize: 13, color: colors.ink2, fontVariantNumeric: "tabular-nums" }}>{right}</div>
    </div>
  );
}
